import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Col,
  Form,
  Image,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import infoImg from "../../../../../../assets/infoimg.png";
import { getAuthUserlocal } from "../../../../../../store/services";

const CalculationOfFees = ({ formData, setFormData, showValidation }) => {
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [userData, setUserData] = useState(null);

  const calculateAdminFee = (amount) => {
    if (amount <= 75000) return 1375;
    if (amount <= 200000) return 2000 + (amount - 75000) * 0.015;
    if (amount <= 700000) return 2800 + (amount - 200001) * 0.014;
    if (amount <= 1200000) return 7000 + (amount - 700001) * 0.013;
    if (amount <= 2200000) return 10000 + (amount - 1200001) * 0.01;
    if (amount <= 6200000) return 12000 + (amount - 2200001) * 0.008;
    if (amount <= 12500000) return 20000 + (amount - 6200001) * 0.0075;
    return 50000 + (amount - 12500001) * 0.005;
  };

  const calculateArbitratorFee = (amount) => {
    if (amount <= 75000) return 1218;
    if (amount <= 200000) return 2343 + (amount - 75001) * 0.03;
    if (amount <= 700000) return 4218 + (amount - 200001) * 0.01;
    if (amount <= 1200000) return 9218 + (amount - 700001) * 0.01;
    if (amount <= 2200000) return 15468 + (amount - 1200001) * 0.0075;
    if (amount <= 6200000) return 24843 + (amount - 2200001) * 0.005;
    return null; // Contact Chamber Desk
  };

  // Get user data and set filing fee based on member type
  useEffect(() => {
    const user = getAuthUserlocal();
    if (user) {
      setUserData(user);
      // memberType and filingFee are now set in ProfileOfClaimant component
    }
  }, [setFormData]);

  useEffect(() => {
    const amount = parseInt(formData.quantumOfClaim) || 0;
    // Always round to 2 decimals
    const adminFee = Math.round(calculateAdminFee(amount) * 100) / 100;
    const arbitratorFeeRaw = calculateArbitratorFee(amount);
    const arbitratorFee = arbitratorFeeRaw !== null ? Math.round(arbitratorFeeRaw * 100) / 100 : null;

    const baseTotal = (formData.filingFee || 0) + (adminFee || 0) + (arbitratorFee || 0);
    const flexibleEnabled = baseTotal > 15000;

    const effectiveAdminFee = formData.selectedFeePlan === "STANDARD"
      ? Math.round(adminFee * 0.9 * 100) / 100
      : formData.selectedFeePlan === "FLEXIBLE"
        ? Math.round(adminFee * 0.4 * 100) / 100
        : adminFee;

    const arbitratorFeeScheduleOne = arbitratorFee ? Math.round(arbitratorFee * 0.25 * 100) / 100 : 0;
    const arbitratorFeeScheduleTwo = arbitratorFee ? Math.round(arbitratorFee * 0.25 * 100) / 100 : 0;
    const arbitratorFeeScheduleThree = arbitratorFee ? Math.round(arbitratorFee * 0.35 * 100) / 100 : 0;
    const arbitratorFeeScheduleFinal = arbitratorFee ? Math.round(arbitratorFee * 0.15 * 100) / 100 : 0;
    const arbitratorFeePayableNow = formData.selectedFeePlan === "FLEXIBLE" ? arbitratorFeeScheduleOne : (arbitratorFee || 0);

    const firstInstallment = formData.selectedFeePlan === "FLEXIBLE" ? Math.round(adminFee * 0.4 * 100) / 100 : 0;
    const secondInstallment = formData.selectedFeePlan === "FLEXIBLE" ? Math.round(adminFee * 0.4 * 100) / 100 : 0;
    const thirdInstallment = formData.selectedFeePlan === "FLEXIBLE" ? Math.round(adminFee * 0.2 * 100) / 100 : 0;

    const standardDiscount = formData.selectedFeePlan === "STANDARD" ? Math.round(adminFee * 0.1 * 100) / 100 : 0;

    // Always round totalFee to 2 decimals
    const totalFee = Math.round(((formData.filingFee || 0) + (effectiveAdminFee || 0) + (arbitratorFeePayableNow || 0)) * 100) / 100;

    setFormData(prev => ({
      ...prev,
      adminFee,
      arbitratorFee,
      arbitratorFeeScheduleOne,
      arbitratorFeeScheduleTwo,
      arbitratorFeeScheduleThree,
      arbitratorFeeScheduleFinal,
      arbitratorFeePayableNow,
      effectiveAdminFee,
      totalFee,
      isFlexibleEnabled: flexibleEnabled,
      firstInstallment,
      secondInstallment,
      thirdInstallment,
      standardDiscount,
      originalAdminFee: adminFee
    }));

    if (!flexibleEnabled && formData.selectedFeePlan === "FLEXIBLE") {
      setFormData(prev => ({
        ...prev,
        selectedFeePlan: ""
      }));
    }
  }, [formData.quantumOfClaim, formData.filingFee, formData.selectedFeePlan, setFormData]);

  const validateField = useCallback((name, value) => {
    let error = "";
    switch (name) {
      case "notes":
        if (!value) error = "Notes are required";
        break;
      case "termsAccepted":
        if (!value) error = "You must accept the terms to proceed";
        break;
      case "feePlanDeclarationAccepted":
        if (!value) error = "You must accept the fee plan declaration to proceed";
        break;
      default:
        if (!value) error = "This field is required";
        break;
    }
    return error;
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    const error = validateField(name, fieldValue);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Validate all fields when showValidation changes
  useEffect(() => {
    if (showValidation) {
      const fields = ["notes", "termsAccepted", "feePlanDeclarationAccepted"];
      const newErrors = {};
      fields.forEach(field => {
        newErrors[field] = validateField(field, formData[field]);
      });
      setFieldErrors(newErrors);
    }
  }, [showValidation, formData, validateField]);

  // Check if all required fields are filled
  useEffect(() => {
    const requiredFields = ["notes", "termsAccepted", "feePlanDeclarationAccepted"];
    const isAllFieldsFilled = requiredFields.every(field => {
      if (field === "termsAccepted" || field === "feePlanDeclarationAccepted") {
        return formData[field] === true;
      }
      return formData[field] && formData[field].trim() !== "";
    });

    setIsFormValid(isAllFieldsFilled);
  }, [formData]);

  // Convert number to English words (USD)
  const numberToWords = (num) => {
    if (!num || Number(num) === 0) return 'Zero US Dollars';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    const convertLessThanOneThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanOneThousand(n % 100) : '');
    };

    const convert = (n) => {
      if (n === 0) return '';
      if (n < 1000) return convertLessThanOneThousand(n);
      if (n < 1000000) return convertLessThanOneThousand(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convertLessThanOneThousand(n % 1000) : '');
      if (n < 1000000000) return convertLessThanOneThousand(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 !== 0 ? ' ' + convertLessThanOneThousand(Math.floor((n % 1000000) / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convertLessThanOneThousand(n % 1000) : '') : '');
      return convertLessThanOneThousand(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 !== 0 ? ' ' + convert(n % 1000000000) : '');
    };

    return convert(Math.floor(Number(num))) + ' US Dollars';
  };

  return (
    <>
      <Row className="mb-3">
        <Form.Group as={Col} controlId="quantumOfClaim">
          <Form.Label className="fw-semibold fs-6 mb-1">
            Quantum of Claim (in USD)
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="button-tooltip-2">Quantum of claim from previous step</Tooltip>}
            >
              {({ ref, ...triggerHandler }) => (
                <Button
                  variant="light"
                  {...triggerHandler}
                  className="d-inline-flex align-items-center"
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                  }}
                >
                  <Image
                    ref={ref}
                    roundedCircle
                    src={infoImg}
                    style={{ height: "3vh" }}
                    alt=""
                  />
                </Button>
              )}
            </OverlayTrigger>
          </Form.Label>
          <Form.Control
            type="text"
            value={formData.quantumOfClaim || ""}
            disabled
            placeholder="Quantum of claim"
          />
        </Form.Group>

        <Form.Group as={Col} controlId="amountInDispute">
          <Form.Label className="fw-semibold fs-6 mb-1">
            Amount in Dispute (in USD)
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="button-tooltip-2">Amount in dispute from previous step</Tooltip>}
            >
              {({ ref, ...triggerHandler }) => (
                <Button
                  variant="light"
                  {...triggerHandler}
                  className="d-inline-flex align-items-center"
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                  }}
                >
                  <Image
                    ref={ref}
                    roundedCircle
                    src={infoImg}
                    style={{ height: "3vh" }}
                    alt=""
                  />
                </Button>
              )}
            </OverlayTrigger>
          </Form.Label>
          <Form.Control
            type="text"
            value={formData.amountInDispute || ""}
            disabled
            placeholder="Amount in dispute"
          />
        </Form.Group>
      </Row>
      <hr />
      <div>
        <h5 className="mb-4">Fee Calculation</h5>
        <Row className="mb-3">
          <Col>
            <div className="fw-semibold">
              (A) Case Filing Fee ({formData.memberType}) <span className="text-danger">*</span>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="button-tooltip-2">
                  *Client/Applicant must be registered as Member of AECCI, if Non-Member then Fees shall be applicable as Below.
                </Tooltip>}
              >
                {({ ref, ...triggerHandler }) => (
                  <Button
                    variant="light"
                    {...triggerHandler}
                    size="sm"
                    className="d-inline-flex align-items-center"
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                    }}
                  >
                    <Image
                      ref={ref}
                      roundedCircle
                      src={infoImg}
                      style={{ height: "2vh" }}
                      alt=""
                    />
                  </Button>
                )}
              </OverlayTrigger>
            </div>
          </Col>
        </Row>

        {/* Filing fee selection checkboxes */}
        <div className="mt-2 mb-3">
          <Row className="mb-2">
            <Col lg="6">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.memberType === "AECCI Member"}
                  disabled
                  style={{ width: "16px", height: "16px" }}
                />
                <span className="fw-semibold">AECCI MEMBER 
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="button-tooltip-2">
                      USD 300 (FOR AECCI-MEMBERS)
                    </Tooltip>}
                  >
                    {({ ref, ...triggerHandler }) => (
                      <Button
                        variant="light"
                        {...triggerHandler}
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                        size="sm"
                      >
                        <Image
                          ref={ref}
                          roundedCircle
                          src={infoImg}
                          style={{ height: "2vh" }}
                          alt=""
                        />
                      </Button>
                    )}
                  </OverlayTrigger>
                </span>
              </div>
            </Col>
            <Col lg="6">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.memberType === "Non Member"}
                  disabled
                  style={{ width: "16px", height: "16px" }}
                />
                <span className="fw-semibold">NON MEMBERS
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="button-tooltip-2">
                      USD 400 (FOR AECCI-NON-MEMBERS)
                    </Tooltip>}
                  >
                    {({ ref, ...triggerHandler }) => (
                      <Button
                        variant="light"
                        {...triggerHandler}
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                        size="sm"
                      >
                        <Image
                          ref={ref}
                          roundedCircle
                          src={infoImg}
                          style={{ height: "2vh" }}
                          alt=""
                        />
                      </Button>
                    )}
                  </OverlayTrigger>
                </span>
              </div>
            </Col>
          </Row>
          {/* Filing Fee Card */}
          <div className="mt-3 p-3 border rounded">
            <Row className="mb-2 align-items-center">
              <Col lg="8">
                <div className="fw-semibold small">Case Filing Fee ({formData.memberType})</div>
                <div className="small">{formData.memberType === "AECCI Member" ? "USD 300 (FOR AECCI-MEMBERS)" : "USD 400 (FOR AECCI-NON-MEMBERS)"}</div>
              </Col>
              <Col lg="4" className="text-end">
                <div className="fw-semibold small">USD {Number(formData.filingFee || 0).toFixed(2)}</div>
              </Col>
            </Row>
          </div>

        </div>

        <hr />
        <Row className="mb-3">
          <Col>
            <div className="fw-semibold"> (B) Administrator Fee <span className="text-danger">*</span>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="button-tooltip-2">
                  AECCI-IAC aims to provide parties with a choice that best suits their needs and preferences when it
                  comes to fulfilling the administrative fee requirements. To accommodate different payment
                  preferences, AECCI-IAC provides two types of fee plans
                </Tooltip>}
              >
                {({ ref, ...triggerHandler }) => (
                  <Button
                    variant="light"
                    {...triggerHandler}
                    className="d-inline-flex align-items-center"
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                    }}
                    size="sm"
                  >
                    <Image
                      ref={ref}
                      roundedCircle
                      src={infoImg}
                      style={{ height: "2vh" }}
                      alt=""
                    />
                  </Button>
                )}
              </OverlayTrigger>
            </div>
          </Col>
        </Row>



        {/* Administration fee selection checkboxes */}
        <div className="mt-2 mb-3">
          <Row className="mb-2">
            <Col lg="6">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.selectedFeePlan === "STANDARD"}
                  onChange={() => setFormData(prev => ({ ...prev, selectedFeePlan: prev.selectedFeePlan === "STANDARD" ? "" : "STANDARD" }))}
                  disabled={false}
                  style={{ width: "16px", height: "16px" }}
                />
                <span className="fw-semibold">STANDARD FEE PLAN
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="button-tooltip-2">
                      Under this payment schedule, the party is required to register as a a Member in AECCI and make a one-
                      time payment of the standard or fixed amount of the administration fee. By opting for this plan, the
                      Centre offers a 10% discount on the administrative fee to the party (If, the Party is Registered Member).
                    </Tooltip>}
                  >
                    {({ ref, ...triggerHandler }) => (
                      <Button
                        variant="light"
                        {...triggerHandler}
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                        size="sm"
                      >
                        <Image
                          ref={ref}
                          roundedCircle
                          src={infoImg}
                          style={{ height: "2vh" }}
                          alt=""
                        />
                      </Button>
                    )}
                  </OverlayTrigger>
                </span>
              </div>
            </Col>
            <Col lg="6">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.selectedFeePlan === "FLEXIBLE"}
                  onChange={() => setFormData(prev => ({ ...prev, selectedFeePlan: prev.selectedFeePlan === "FLEXIBLE" ? "" : "FLEXIBLE" }))}
                  disabled={!formData.isFlexibleEnabled}
                  style={{ width: "16px", height: "16px" }}
                />
                <span className="fw-semibold">FLEXIBLE FEE PLAN
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="button-tooltip-2">
                      The AECCI-IAC offers a Flexible Fee Schedule as an alternative payment option. Under this schedule,
                      the party has the flexibility to pay the administrative fee in installments. Specifically, the payment is
                      divided into three parts: 40% of the fee is due at the time of case filing, another 40% is due at the
                      time of proceeding before the hearing, and the final 20% is due upon the conclusion of the
                      arbitration, prior to the issuance of the award. This allows the party to manage their payments in a
                      more flexible and easy manner.
                    </Tooltip>}
                  >
                    {({ ref, ...triggerHandler }) => (
                      <Button
                        variant="light"
                        {...triggerHandler}
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                        size="sm"
                      >
                        <Image
                          ref={ref}
                          roundedCircle
                          src={infoImg}
                          style={{ height: "2vh" }}
                          alt=""
                        />
                      </Button>
                    )}
                  </OverlayTrigger>
                </span>
              </div>
            </Col>
          </Row>
          {/* Administrator Fee Card */}
          <div className="mt-3 p-3 border rounded">
            {!formData.selectedFeePlan ? (
              <Row className="mb-2 align-items-center">
                <Col lg="8">
                  <div className="fw-semibold small">Administrator Fee </div>
                  <div className="small">Default fee based on quantum of claim</div>
                </Col>
                <Col lg="4" className="text-end">
                  <div className="fw-semibold small">USD {Number(formData.adminFee || 0).toFixed(2)}</div>
                </Col>
              </Row>
            ) : formData.selectedFeePlan === "STANDARD" ? (
              <>
                <Row className="mb-2 align-items-center">
                  <Col lg="8">
                    <div className="fw-semibold small">Discount Applied</div>
                    <div className="small">10% discount for AECCI Members</div>
                  </Col>
                  <Col lg="4" className="text-end">
                    <div className="fw-semibold small">USD {formData.standardDiscount.toFixed(2)}</div>
                  </Col>
                </Row>
                <Row className="mb-2 align-items-center">
                  <Col lg="8">
                    <div className="fw-semibold small">Total Administrator Fee</div>
                    <div className="small">After applying 10% discount</div>
                  </Col>
                  <Col lg="4" className="text-end">
                    <div className="fw-semibold small">USD {(formData.adminFee - formData.standardDiscount).toFixed(2)}</div>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Row className="mb-2 align-items-center">
                  <Col lg="8">
                    <div className="fw-semibold small">First Installment</div>
                    <div className="small">40% due on: at the time of Case Filing</div>
                  </Col>
                  <Col lg="4" className="text-end">
                    <div className="fw-semibold small">USD {formData.firstInstallment.toFixed(2)}</div>
                  </Col>
                </Row>
                <Row className="mb-2 align-items-center">
                  <Col lg="8">
                    <div className="fw-semibold small">Second Installment</div>
                    <div className="small">40% due on: at the time of proceedings, before hearing</div>
                  </Col>
                  <Col lg="4" className="text-end">
                    <div className="fw-semibold small">USD {formData.secondInstallment.toFixed(2)}</div>
                  </Col>
                </Row>
                <Row className="mb-2 align-items-center">
                  <Col lg="8">
                    <div className="fw-semibold small">Third Installment</div>
                    <div className="small">20% due on: prior to issuance of award</div>
                  </Col>
                  <Col lg="4" className="text-end">
                    <div className="fw-semibold small">USD {formData.thirdInstallment.toFixed(2)}</div>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </div>

        <hr />

        <Row className="mb-3">
          <Col>
            <div className="fw-semibold"> (C) Arbitrator's Fee <span className="text-danger">*</span>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="button-tooltip-2">
                  The fee calculated in accordance with the Article 36 of AECCI-IAC Rules and the amount payable to arbitrator and Additional Fees for the Arbitral Tribunal (Per Arbitrator). According to the Arbitrators Fee Schedule, the following deposit percentages should be made at specific stages of the arbitration process
                </Tooltip>}
              >
                {({ ref, ...triggerHandler }) => (
                  <Button
                    variant="light"
                    {...triggerHandler}
                    className="d-inline-flex align-items-center"
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                    }}
                    size="sm"
                  >
                    <Image
                      ref={ref}
                      roundedCircle
                      src={infoImg}
                      style={{ height: "2vh" }}
                      alt=""
                    />
                  </Button>
                )}
              </OverlayTrigger>
            </div>
          </Col>
        </Row>

        {/* Arbitrator's Fee Card */}
        <div className="mt-3 p-3 border rounded">
          {formData.selectedFeePlan !== "FLEXIBLE" ? (
            <Row className="mb-2 align-items-center">
              <Col lg="8">
                <div className="fw-semibold small">Arbitrator's Fee</div>
                <div className="small">Calculated based on the quantum of claim</div>
              </Col>
              <Col lg="4" className="text-end">
                <div className="fw-semibold small">
                  {formData.arbitratorFee
                    ? `USD ${Number(formData.arbitratorFee || 0).toFixed(2)}`
                    : "Contact Chamber Desk"}
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <Row className="mb-2 align-items-center">
                <Col lg="8">
                  <div className="fw-semibold small">Schedule One</div>
                  <div className="small">25% due on: At the time of filing the statement of claim</div>
                </Col>
                <Col lg="4" className="text-end">
                  <div className="fw-semibold small">USD {Number(formData.arbitratorFeeScheduleOne || 0).toFixed(2)}</div>
                </Col>
              </Row>
              <Row className="mb-2 align-items-center">
                <Col lg="8">
                  <div className="fw-semibold small">Schedule Two</div>
                  <div className="small">25% due on: at the time of Preliminary Hearing</div>
                </Col>
                <Col lg="4" className="text-end">
                  <div className="fw-semibold small">USD {Number(formData.arbitratorFeeScheduleTwo || 0).toFixed(2)}</div>
                </Col>
              </Row>
              <Row className="mb-2 align-items-center">
                <Col lg="8">
                  <div className="fw-semibold small">Schedule Three</div>
                  <div className="small">35% due on: Prior to the proceedings of Conventional Hearings</div>
                </Col>
                <Col lg="4" className="text-end">
                  <div className="fw-semibold small">USD {Number(formData.arbitratorFeeScheduleThree || 0).toFixed(2)}</div>
                </Col>
              </Row>
              <Row className="mb-2 align-items-center">
                <Col lg="8">
                  <div className="fw-semibold small">Final Schedule</div>
                  <div className="small">15% due on: prior to issuance of award</div>
                </Col>
                <Col lg="4" className="text-end">
                  <div className="fw-semibold small">USD {Number(formData.arbitratorFeeScheduleFinal || 0).toFixed(2)}</div>
                </Col>
              </Row>
            </>
          )}
        </div>
        <hr />
        {/* Total Payable Summary */}
        <div className="mt-3 p-3 border rounded">
          <div className="fw-semibold mb-2">Total Payable Now</div>
          <div className="d-flex justify-content-between small">
            <span>Case Filing Fee</span>
            <span>USD {Number(formData.filingFee || 0).toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between small">
            <span>Administrator Fee{formData.selectedFeePlan === "FLEXIBLE" ? " (First Installment)" : ""}</span>
            <span>USD {Number((formData.effectiveAdminFee ?? formData.adminFee) || 0).toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between small">
            <span>Arbitrator's Fee{formData.selectedFeePlan === "FLEXIBLE" ? " (Schedule One)" : ""}</span>
            <span>
              {formData.arbitratorFee
                ? `USD ${Number((formData.selectedFeePlan === "FLEXIBLE" ? formData.arbitratorFeeScheduleOne : formData.arbitratorFee) || 0).toFixed(2)}`
                : "Contact Chamber Desk"}
            </span>
          </div>
          {formData.selectedFeePlan && (
            <div className="d-flex justify-content-between small mt-2">
              <span>Selected Plan</span>
              <span>{formData.selectedFeePlan}</span>
            </div>
          )}
          <hr className="my-2" />
          <div className="d-flex justify-content-between">
            <strong>Total Payable Now</strong>
            <strong>USD {Number(formData.totalFee || 0).toFixed(2)}</strong>
          </div>
          <div className="small mt-2">{numberToWords(formData.totalFee)}</div>
        </div>
      </div>

      <Form.Group controlId="notes" className="mb-3 mt-3">
        <Form.Label className="fw-semibold fs-6 mb-1">Notes <span className="text-danger">*</span></Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="notes"
          value={formData.notes || ""}
          onChange={handleInputChange}
          placeholder="Enter any additional notes here"
          isInvalid={showValidation && !!fieldErrors.notes}
        />
        <Form.Control.Feedback type="invalid">
          {fieldErrors.notes}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3 mt-3" id="formGridCheckbox">
        <div className="d-flex align-items-start gap-2">
          <input
            type="checkbox"
            name="termsAccepted"
            checked={formData.termsAccepted || false}
            onChange={handleInputChange}
            style={{ width: "16px", height: "16px", marginTop: "2px" }}
            required
          />
          <div>
            <label className="mb-0" style={{ fontSize: "14px", lineHeight: "1.4" }}>
              I hereby affirm that all the information provided herein is accurate and true to the best of my knowledge and I shall adhere to all the regulations of Asian Exporters Chamber of Commerce and Industry.
            </label>
            {showValidation && !!fieldErrors.termsAccepted && (
              <div className="text-danger mt-1 small">
                {fieldErrors.termsAccepted}
              </div>
            )}
          </div>
        </div>
      </Form.Group>

      <Form.Group className="mb-3 mt-3" id="feePlanDeclarationCheckbox">
        <div className="d-flex align-items-start gap-2">
          <input
            type="checkbox"
            name="feePlanDeclarationAccepted"
            checked={formData.feePlanDeclarationAccepted || false}
            onChange={(e) => setFormData(prev => ({ ...prev, feePlanDeclarationAccepted: e.target.checked }))}
            style={{ width: "16px", height: "16px", marginTop: "2px" }}
            required
          />
          <div>
            <label className="mb-0" style={{ fontSize: "14px", lineHeight: "1.4" }}>
              I/ We hereby declare that I/ We have carefully considered the administrative fee options provided by AECCI-IAC
              and have selected the Standard / Flexible fee plan that best suits my/our preferences and
              circumstances for the resolution of the dispute. I/ We acknowledge and agree to comply with the payment terms
              and conditions associated with the chosen administrative fee plan.
            </label>
            {showValidation && !formData.feePlanDeclarationAccepted && (
              <div className="text-danger mt-1 small">
                You must accept the fee plan declaration to proceed
              </div>
            )}
          </div>
        </div>
      </Form.Group>

      {!isFormValid && showValidation && (
        <div className="text-danger mt-3">
          Please fill all required fields correctly before proceeding.
        </div>
      )}
    </>
  );
};

export default CalculationOfFees;